import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { assignLead } from '@/api/leads.api';
import { ApiError } from '@/api/errors';
import { AgentCombobox } from '@/components/form/AgentCombobox';
import { showSuccessToast } from '@/lib/toast';
import type { LeadResponse } from '@/types/shared';
import './LeadPanels.css';

interface LeadAssignPanelProps {
  lead: LeadResponse;
  canManage: boolean;
  onSaved: (updated: LeadResponse) => void;
}

/**
 * Assign / reassign a lead to a Sales Agent (`PATCH /leads/{id}/assign`, Sales Manager/Admin only).
 * The agent list comes from `GET /leads/assignable-agents` (active Sales Agents, Manager/Admin).
 */
export function LeadAssignPanel({ lead, canManage, onSaved }: LeadAssignPanelProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  async function handleAssign() {
    const id = value.trim();
    setFieldError(null);
    setPanelError(null);
    setSavedFlash(false);
    if (!id) return;

    setSubmitting(true);
    try {
      const updated = await assignLead(lead.leadId, id);
      onSaved(updated);
      setValue('');
      setSavedFlash(true);
      showSuccessToast(t('leads.detail.assignSuccess'));
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      if (apiError.code === 'ASSIGNEE_NOT_SALES_AGENT') {
        setFieldError(t('leads.detail.errors.assigneeNotSalesAgent'));
      } else if (apiError.code === 'LEAD_ALREADY_CONVERTED') {
        setPanelError(t('leads.detail.errors.assignAlreadyConverted'));
      } else {
        setPanelError(apiError.message || t('leads.detail.errors.assignFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="fa-form-section fa-lead-panel">
      <h2 className="fa-form-section__title">{t('leads.detail.assignTitle')}</h2>

      <p className="fa-lead-panel__assigned-to">
        <span className="fa-field__label">{t('leads.detail.assignedToLabel')}</span>{' '}
        {lead.assignedEmployee ? (
          lead.assignedEmployee.fullName
        ) : (
          <span className="fa-lead-panel__muted">{t('leads.unassignedLabel')}</span>
        )}
      </p>

      {canManage && (
        <>
          <div className="fa-field">
            <label htmlFor="lead-assignTo">{t('leads.detail.assignPlaceholder')}</label>
            <AgentCombobox
              id="lead-assignTo"
              value={value}
              disabled={submitting}
              ariaInvalid={Boolean(fieldError)}
              placeholder={t('leads.detail.assignSelectPlaceholder')}
              onChange={(v) => {
                setFieldError(null);
                setSavedFlash(false);
                setValue(v);
              }}
            />
            {fieldError && <p className="fa-field__error">{fieldError}</p>}
          </div>

          <div className="fa-lead-panel__actions">
            <button
              type="button"
              className="fa-btn fa-btn--secondary"
              disabled={!value.trim() || submitting}
              onClick={handleAssign}
            >
              {submitting ? t('leads.detail.assigning') : t('leads.detail.assignSubmit')}
            </button>
            {savedFlash && (
              <span className="fa-lead-panel__saved" role="status">
                {t('leads.detail.assignSuccess')}
              </span>
            )}
            {panelError && (
              <p className="fa-field__error" role="alert">
                {panelError}
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
}
